import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import Row from 'components/Common/Row';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Dropdown from 'components/Inputs/Dropdown';
import Typography from 'kingpin/atoms/Typography';
import Loading from 'components/Loading';
import Alert from 'kingpin/Alert';
import Button from 'kingpin/atoms/Button';
import STORE from 'store';
import AccountPickerContext from 'contexts/AccountPickerContext';
import ToastContext from 'contexts/ToastContext';

import useUnMappedUsers from './useUnMappedUsers';
import FilterPlatesProvider from '../../../contextProviders/FilterPlatesProvider';
import FilterPlates from '../../../components/FilterPlates';
import filterPlateTypeCheckers from '../../../types/filterPlateTypeCheckers';
import usePrimaryFieldValueOptions from './usePrimaryFieldValueOptions';
import Icon from '../../../kingpin/atoms/Icon';
import filterTypeCheckers from '../../../components/FilterPlateForm/filterTypeCheckers';
import useAccessToOtherMappedUserIdsOptions from './useAccessToOtherMappedUserIdsOptions';
import Tooltip from '../../../components/Tooltip';
import { CurrentUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/CurrentUserContentSettingsProvider';

const useUserOptions = ({
  mappedUser,
  entityId,
  entityAppId,
}: {
  mappedUser: MappedUser | undefined;
  entityId: string;
  entityAppId: string;
}) => {
  const [selectedUser, setSelectedUser] = useState<
    UserManagement.SignedUpUser | UserManagement.PendingUser | undefined
  >(mappedUser ? mappedUser.user : undefined);
  const { unMappedUsers, isLoading } = useUnMappedUsers(entityId, entityAppId);
  const [options, setOptions] = useState<DropdownOption[]>([]);

  const getOptions = useCallback((): DropdownOption[] => {
    const baseUsers = unMappedUsers.map((user) => ({
      label: user.displayName,
      isSelected: selectedUser && selectedUser.id === user.id,
      onSelected: () => {
        setSelectedUser(user);
      },
      key: user.id,
    }));
    const sorted = _.sortBy(baseUsers, 'label');

    if (mappedUser) {
      sorted.push({
        label: mappedUser.user.displayName,
        isSelected: true,
        onSelected: () => {},
        key: mappedUser.user.id,
      });
    }

    return sorted;
  }, [mappedUser, selectedUser, unMappedUsers]);

  useEffect(() => {
    setOptions(getOptions());
  }, [getOptions]);

  return {
    options,
    isLoading,
    selectedUser,
  };
};

/**
 * This is a V2 - Engagement portal mapping.
 *
 * For V3, see {@link ProductAppUsersScreen}/AddMappedUserForm
 */
const AddMappedUserForm = ({
  entity,
  entityAppId,
  entityPortalName,
  close,
  setMappedUsers,
  mappedUser,
  mappedUsers,
}: {
  entity: EntityDetails.Entity;
  entityAppId: string;
  setMappedUsers: React.Dispatch<React.SetStateAction<MappedUser[]>>;
  entityPortalName: string;
  close: () => void;
  mappedUser?: MappedUser;
  mappedUsers: MappedUser[];
}) => {
  const { showToast } = useContext(ToastContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { refreshContentSettings } = useContext(
    CurrentUserContentSettingsContext,
  );
  const {
    options: userOptions,
    isLoading: isLoadingUsers,
    selectedUser,
  } = useUserOptions({ mappedUser, entityId: entity.id, entityAppId });
  const [primaryFieldValue, setPrimaryFieldValue] = useState<
    string | undefined
  >(mappedUser ? mappedUser.filterValue : undefined);
  const [accessToOtherMappedUserIds, setAccessToOtherMappedUserIds] = useState<
    string[]
  >(() => {
    if (mappedUser) {
      return mappedUser.accessToOtherMappedUserIds;
    }
    return [];
  });
  const mappedUsersOtherThanCurrentUser = useMemo((): MappedUser[] => {
    if (!mappedUser) {
      return mappedUsers;
    }
    return mappedUsers.filter((mu) => mu.user.id !== mappedUser.user.id);
  }, [mappedUser, mappedUsers]);
  const accessToOtherMappedUserIdsOptions =
    useAccessToOtherMappedUserIdsOptions({
      accessToOtherMappedUserIds,
      setAccessToOtherMappedUserIds,
      mappedUsers: mappedUsersOtherThanCurrentUser,
    });
  const [plates, setPlates] = useState<FilterPlateType[]>(() => {
    if (!mappedUser) {
      return [];
    }

    if (!mappedUser.additionalFilters) {
      return [];
    }

    return mappedUser.additionalFilters;
  });
  const additionalPrimaryFieldValues = useMemo(() => {
    if (!entity) {
      return [];
    }

    return plates.reduce((acc, plate) => {
      if (
        plate.type === 'Fixed' &&
        plate.fixedValue.field === entity.primaryField
      ) {
        const { fixedValue } = plate;
        if (!filterTypeCheckers.isTextFilter(fixedValue)) {
          return acc;
        }

        return _.uniq([...acc, ...fixedValue.keywordValues]);
      }
      return acc;
    }, [] as string[]);
  }, [entity, plates]);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

  const { primaryFieldValueOptions, isLoadingOptions } =
    usePrimaryFieldValueOptions({
      entityId: entity.id,
      primaryFieldValue,
      setPrimaryFieldValue,
      additionalPrimaryFieldValues,
    });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isValid = !!selectedUser && !!primaryFieldValue;

  const getCurrentMappings = useCallback(
    async ({
      selectedUser,
    }: {
      selectedUser: UserManagement.SignedUpUser | UserManagement.PendingUser;
    }): Promise<UserManagement.EngagementContentMapping[]> => {
      const doc = await STORE.userManagement
        .getUserContentSettingsRef({
          accountId: selectedAccountId,
          userId: selectedUser.id,
        })
        .get();
      const data = doc.data();
      if (!data) {
        return [];
      }
      if (data.mode === 'normal') {
        return [];
      }
      return data.mappings;
    },
    [selectedAccountId],
  );

  const onSaveClicked = useCallback(async () => {
    if (!isValid || isLoading || !selectedUser || !primaryFieldValue) {
      return;
    }

    const additionalFilters = plates.filter(filterPlateTypeCheckers.isFixed);
    const currentMappings = await getCurrentMappings({ selectedUser });

    const newContentSettings: UserManagement.ContentSettings = {
      mode: 'engagement',
      mappings: [
        ...currentMappings.filter(
          (m) => !(m.entityId === entity.id && m.entityAppId === entityAppId),
        ),
        {
          primaryFieldValue,
          entityId: entity.id,
          entityAppId,
          additionalFilters,
          accessToOtherMappedUserIds,
        },
      ],
    };

    STORE.userManagement
      .getUserContentSettingsRef({
        accountId: selectedAccountId,
        userId: selectedUser.id,
      })
      .set(newContentSettings)
      .then(() => {
        setMappedUsers((mappedUsers) => {
          if (mappedUsers.some((m) => m.user.id === selectedUser.id)) {
            return mappedUsers.map((u): MappedUser => {
              if (
                u.user.id === selectedUser.id &&
                u.entityAppId === entityAppId
              ) {
                return {
                  ...u,
                  filterValue: primaryFieldValue,
                  additionalFilters,
                  accessToOtherMappedUserIds,
                  entityId: entity.id,
                  entityAppId,
                };
              } else {
                return u;
              }
            });
          } else {
            return [
              ...mappedUsers,
              {
                user: selectedUser,
                filterValue: primaryFieldValue,
                additionalFilters,
                accessToOtherMappedUserIds,
                entityId: entity.id,
                entityAppId,
              },
            ];
          }
        });
        showToast(`${entityPortalName} added`);
        refreshContentSettings();
        setIsLoading(false);
        close();
      });
  }, [
    isValid,
    isLoading,
    selectedUser,
    primaryFieldValue,
    plates,
    getCurrentMappings,
    entity.id,
    entityAppId,
    accessToOtherMappedUserIds,
    selectedAccountId,
    setMappedUsers,
    showToast,
    entityPortalName,
    refreshContentSettings,
    close,
  ]);

  const textFieldModeGreenList = useMemo(() => {
    if (!entity) {
      return undefined;
    }

    return {
      [entity.primaryField]: ['is one of' as 'is one of'],
    };
  }, [entity]);

  const datasetGreenList = useMemo(() => {
    return [entity.entityDataset];
  }, [entity.entityDataset]);

  if (isLoadingUsers || isLoadingOptions) {
    return (
      <Form>
        <FormHeader title={``} onClose={close} />
        <FormContent>
          <Loading />
        </FormContent>
      </Form>
    );
  }

  if (!isLoadingUsers && !isLoadingOptions && userOptions.length === 0) {
    return (
      <Form>
        <FormHeader title={`Add ${entityPortalName}`} onClose={close} />
        <FormContent>
          <Alert
            type="Info"
            body={`Every user who can be mapped to this View has already been mapped. Admin users can not be mapped to a View.`}
          />
        </FormContent>
      </Form>
    );
  }

  return (
    <Form>
      <FormHeader
        title={
          mappedUser ? `Update ${entityPortalName}` : `Add ${entityPortalName}`
        }
        onClose={close}
      />
      <FormContent>
        <div style={{ marginBottom: 16 }}>
          <Row centerAlign style={{ gap: 16 }}>
            <div>
              <Row style={{ marginBottom: 4, gap: 4 }} centerAlign>
                <Typography.Body type="Label">User</Typography.Body>
                <Tooltip
                  content={'Please note that only Basic users can be selected'}
                >
                  <Icon icon={'info'} color={'var(--color-grey-4)'} />
                </Tooltip>
              </Row>
              <Dropdown
                options={userOptions}
                placeholder="Select a User"
                isSearchEnabled
                isDisabled={!!mappedUser}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4, opacity: 0 }}>
                <Typography.Body type="Label">x</Typography.Body>
              </div>
              <Icon icon={'arrow-right'} color={'var(--color-text-muted)'} />
            </div>
            <div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body type="Label">{`${entityPortalName}`}</Typography.Body>
              </div>
              <Dropdown
                testId={`primary-field-picker`}
                options={primaryFieldValueOptions}
                placeholder={`Select a ${entityPortalName}`}
                isSearchEnabled
              />
            </div>
          </Row>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Row spaceBetween>
            <div />
            <Button
              size="Small"
              label="Advanced"
              iconAfter={isAdvancedOpen ? 'chevron-up' : 'chevron-down'}
              type={'Ghost'}
              onClick={() => {
                setIsAdvancedOpen((c) => !c);
              }}
            />
          </Row>
        </div>
        {isAdvancedOpen && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div>
                <Typography.Body type="Label">{`Add More Data`}</Typography.Body>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body
                  type="Annotation"
                  color={'var(--color-grey-3)'}
                >
                  {`Allow access the following ${entityPortalName}s views`}
                </Typography.Body>
              </div>
              <Dropdown
                options={accessToOtherMappedUserIdsOptions}
                placeholder={`Select additional ${entityPortalName}s`}
                isSearchEnabled
                isMulti
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div>
                <Typography.Body type="Label">Filters</Typography.Body>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body
                  type="Annotation"
                  color={'var(--color-grey-3)'}
                >
                  {`Additional filters for ${selectedUser ? `${selectedUser.displayName}'s` : 'this'} view`}
                </Typography.Body>
              </div>
              <FilterPlatesProvider
                drillDowns={plates}
                setDrillDowns={setPlates}
                dataTypes={datasetGreenList}
                textFieldModeGreenList={textFieldModeGreenList}
              >
                <FilterPlates />
              </FilterPlatesProvider>
            </div>
          </>
        )}
      </FormContent>
      <Row centerAlign spaceBetween>
        <div />
        <Button
          type="Primary"
          onClick={onSaveClicked}
          isDisabled={!isValid}
          label={mappedUser ? 'Update User' : 'Add User'}
          size={'Small'}
        />
      </Row>
    </Form>
  );
};

export default AddMappedUserForm;
